import { useState, useEffect, useCallback, useRef } from 'react';
import { Patient, PatientStatus, SurgerySubStatus, SurgeryCondition } from '../types';
import * as cloudflareService from '../services/cloudflareService';
import { getPatients, STORAGE_KEY_PATIENTS, getRooms, STORAGE_KEY_ROOMS } from '../services/storageService';

// Queue item structure
interface BatchUpdateItem {
  id: string;
  changes: Partial<Patient>;
}

export const useBatchQueue = (hospitalId: string) => {
  const [queue, setQueue] = useState<Map<string, Partial<Patient>>>(new Map());
  const timerRef = useRef<number | null>(null);

  const flushQueue = useCallback(async () => {
    if (queue.size === 0) return;

    // Snapshot current queue and clear it to allow new incoming updates during flush
    const updatesArray: BatchUpdateItem[] = Array.from(queue.entries()).map(([id, changes]) => ({
      id,
      changes
    }));
    
    setQueue(new Map()); // clear visual queue

    try {
      // 1. Send the batch to the backend
      const result = await cloudflareService.batchUpdatePatients(updatesArray);
      
      if (!result.success) {
        throw new Error(result.error || 'Batch operation failed on server');
      }

      // 2. We don't need to manually update storage here 
      // because the Optimistic UI (queueUpdate) already updated local storage!
      // Any failures would require a rollback, which we could implement later if needed.
    } catch (err) {
      console.error('Failed to flush patient updates batch:', err);
      window.dispatchEvent(new Event('patients-sync-required'));
      throw err;
    }
  }, [queue]);

  useEffect(() => {
    // If there are items in the queue, set a timer to flush them
    if (queue.size > 0) {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      
      timerRef.current = window.setTimeout(() => {
        flushQueue();
      }, 2000); // 2 seconds debounce
    }

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [queue, flushQueue]);

  const queueUpdate = useCallback(async (patientId: string, updates: Partial<Patient>) => {
    // 1. OPTIMISTIC UI UPDATE
    const localPatients = getPatients();
    const patientIndex = localPatients.findIndex(p => p.id === patientId);
    
    if (patientIndex >= 0) {
      const patient = localPatients[patientIndex];
      const updatedPatient = { ...patient, ...updates, lastEditedAt: new Date().toISOString() };
      
      // Auto-logs logic (mirroring backend/storageService)
      if (updates.currentStatus && updates.currentStatus === PatientStatus.SURGERY) {
        if (!updatedPatient.currentSubStatus) updatedPatient.currentSubStatus = SurgerySubStatus.INCISION;
        if (!updatedPatient.currentCondition) updatedPatient.currentCondition = SurgeryCondition.NORMAL;
      } else if (updates.currentStatus && updates.currentStatus !== PatientStatus.SURGERY) {
        updatedPatient.currentSubStatus = undefined;
        updatedPatient.currentCondition = SurgeryCondition.NORMAL;
      }

      if (updates.currentStatus !== undefined || updates.surgeryProgress !== undefined) {
         updatedPatient.logs = [
           ...updatedPatient.logs,
           {
             status: updatedPatient.currentStatus,
             subStatus: updatedPatient.currentSubStatus,
             condition: updatedPatient.currentCondition,
             timestamp: new Date().toISOString(),
             updatedBy: 'Staff'
           }
         ];
      }

      // If patient is exiting or recovering, clear from room locally
      if (updates.currentStatus === PatientStatus.EXIT || updates.currentStatus === PatientStatus.RECOVERY) {
        if (updates.currentStatus === PatientStatus.EXIT) {
          updatedPatient.dischargeTime = new Date().toISOString();
        }
        if (updatedPatient.room) {
           const localRooms = getRooms();
           const updatedRooms = localRooms.map(r => r.id === updatedPatient.room ? { ...r, currentPatientId: null } : r);
           localStorage.setItem(STORAGE_KEY_ROOMS, JSON.stringify(updatedRooms));
        }
      }

      localPatients[patientIndex] = updatedPatient;
      localStorage.setItem(STORAGE_KEY_PATIENTS, JSON.stringify(localPatients));
      
      // Dispatch custom event to trigger React state updates in components
      window.dispatchEvent(new CustomEvent('patient-updated-optimistically', { detail: updatedPatient }));
    }

    // 2. ENQUEUE FOR BATCHING
    setQueue(prev => {
      const newQueue = new Map(prev);
      const existing = (newQueue.get(patientId) || {}) as Partial<Patient>;
      
      // Merge latest changes on top
      newQueue.set(patientId, { ...existing, ...updates });
      return newQueue;
    });

  }, []);

  return { queueUpdate, flushQueue, queueSize: queue.size };
};
